import { useEffect, useRef, useState } from 'react';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  PlusIcon,
  TrashIcon,
  ArrowsUpDownIcon,
  ArrowUpTrayIcon,
} from '@heroicons/react/24/outline';
import { contentApi } from '../../services/api';
import { useOrganizationStore } from '../../stores/organization';
import toast from 'react-hot-toast';
import { isAxiosError } from 'axios';
import { LoadingSpinner } from '../common';
import CatalogCategoriesEditor, { CategoryTile } from './CatalogCategoriesEditor';
import { CatalogSpaces, ProductDetailStructure, ProductDetailStructureEditor, SpacesEditor } from './CatalogSpacesEditor';

// ---------------------------------------------------------------------------
// Types (mirror backend CatalogSiteConfig)
// ---------------------------------------------------------------------------

interface HeroSlide {
  id: string;
  subtitle: string;
  title: string;
  description: string;
  image: string;
  slug: string;
}


interface BentoCard {
  id: string;
  icon: string;
  heading: string;
  body: string;
  image?: string;
  overlay_heading?: string;
  overlay_body?: string;
  /** Enlace opcional de la tarjeta. Una URL externa se abre en pestaña nueva. */
  link?: { type: 'url'; href: string };
}

interface FooterColumn {
  heading: string;
  links: { label: string; href: string }[];
}

interface PageContent {
  slug: string;
  title: string;
  content: string;
  active: boolean;
}

interface CatalogSiteConfig {
  hero_slides: HeroSlide[];
  categories_grid: CategoryTile[];
  home_sections: {
    categories_heading: string;
    categories_description: string;
    featured_heading: string;
    bento_heading: string;
    more_heading: string;
    cta_heading: string;
    cta_description: string;
    cta_button: string;
    ideas_heading?: string;
    ideas_description?: string;
    solutions_heading?: string;
    solutions_description?: string;
  };
  bento_cards: BentoCard[];
  /** Mostrar la sección «Ideas que transforman» en la home. Ausente/true = visible. */
  show_ideas?: boolean;
  cta_image: string;
  // Curated product lists for the home page: arrays of product slugs that
  // populate the "Ideas que Transforma" and "Más Soluciones" carousels.
  // Leave empty to hide the section in the public site.
  ideas_products?: string[];
  solutions_products?: string[];
  footer_links: { columns: FooterColumn[] };
  page_meta: {
    title: string;
    title_template: string;
    description: string;
  };
  ui_strings: {
    catalog_subtitle: string;
    search_placeholder: string;
    results_suffix: string;
    detail_about_heading: string;
    detail_specs_heading: string;
    detail_cta_heading: string;
    detail_cta_body: string;
    detail_related_heading: string;
    nav_home: string;
    nav_catalog: string;
    footer_description: string;
  };
  pages: PageContent[];
  /** Qué enseña la ficha de cada enlace (VIDA-372). */
  product_detail?: ProductDetailStructure;
  /** Espacios adicionales de la portada (VIDA-372). */
  spaces?: CatalogSpaces;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const uid = () => `id-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const inputClass = 'w-full rounded-lg border-gray-300 shadow-sm text-sm focus:ring-vida-500 focus:border-vida-500';
const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

// ---------------------------------------------------------------------------
// Accordion section wrapper
// ---------------------------------------------------------------------------

function Section({ title, subtitle, defaultOpen, children }: {
  title: string;
  subtitle?: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen ?? false);
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center justify-between w-full px-6 py-4 text-left hover:bg-gray-50 transition-colors"
      >
        <div>
          <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
          {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
        </div>
        {open ? <ChevronUpIcon className="w-5 h-5 text-gray-400" /> : <ChevronDownIcon className="w-5 h-5 text-gray-400" />}
      </button>
      {open && <div className="px-6 pb-6 border-t border-gray-100 pt-4">{children}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Image upload field — reusable input that accepts either a pasted URL or an
// uploaded file. Used by Hero Slides, Bento Cards and the CTA image. On
// upload, requests a presigned URL from the backend, PUTs the file to MinIO
// and feeds the public URL back into the parent's `onChange`. The text input
// remains editable so pasting external URLs still works.
// ---------------------------------------------------------------------------

function ImageUploadField({ value, onChange, placeholder, previewSize = 'sm' }: {
  value: string;
  onChange: (url: string) => void;
  placeholder?: string;
  previewSize?: 'sm' | 'md';
}) {
  const orgId = useOrganizationStore((s) => s.selectedOrg?._id);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const previewClass = previewSize === 'md'
    ? 'w-24 h-16'
    : 'w-16 h-10';

  const handlePick = () => fileInputRef.current?.click();

  const handleFile = async (file: File) => {
    if (!orgId) {
      toast.error('Selecciona una organización primero');
      return;
    }
    setUploading(true);
    try {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const ts = Date.now();
      const objectName = `catalog-site/${ts}-${safeName}`;
      const presignRes = await contentApi.getUploadUrl(orgId, objectName);
      const { uploadUrl, publicUrl } = presignRes.data as { uploadUrl: string; publicUrl: string };
      const putRes = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      });
      if (!putRes.ok) {
        const t = await putRes.text().catch(() => '');
        throw new Error(`MinIO ${putRes.status}: ${t.slice(0, 120)}`);
      }
      onChange(publicUrl);
      toast.success('Imagen subida');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'unknown';
      toast.error(`Error subiendo imagen: ${msg}`);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div className="flex items-center gap-2">
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`flex-1 ${inputClass}`}
        placeholder={placeholder || 'https://... o pulsa Subir'}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void handleFile(f);
        }}
      />
      <button
        type="button"
        onClick={handlePick}
        disabled={uploading}
        title="Subir imagen desde tu equipo"
        className="inline-flex items-center gap-1.5 px-2.5 py-2 text-xs font-medium bg-vida-600 text-white rounded-lg hover:bg-vida-700 disabled:opacity-60 flex-shrink-0">
        {uploading ? <LoadingSpinner size="sm" /> : <ArrowUpTrayIcon className="w-4 h-4" />}
        Subir
      </button>
      {value && (
        <div className={`${previewClass} rounded overflow-hidden bg-gray-200 flex-shrink-0`}>
          <img src={value} alt="" className="w-full h-full object-cover" />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Slug list editor — textarea-driven array of product slugs used to populate
// curated home sections like "Ideas que Transforma" and "Mas Soluciones".
// Stores one slug per line, trimming and deduplicating on change.
// ---------------------------------------------------------------------------

function SlugListEditor({ label, help, value, onChange }: {
  label: string;
  help?: string;
  value: string[];
  onChange: (arr: string[]) => void;
}) {
  const text = value.join('\n');
  return (
    <div>
      <label className={labelClass}>{label}</label>
      <textarea
        rows={Math.max(3, value.length + 1)}
        value={text}
        onChange={(e) => {
          const arr = e.target.value
            .split('\n')
            .map((s) => s.trim())
            .filter(Boolean);
          // De-dup while preserving order.
          const seen = new Set<string>();
          const dedup: string[] = [];
          for (const s of arr) {
            if (!seen.has(s)) {
              seen.add(s);
              dedup.push(s);
            }
          }
          onChange(dedup);
        }}
        className={`${inputClass} font-mono text-xs`}
        placeholder={'paisaje-de-guadix\nrutas-del-marquesado\n...'}
      />
      {help && <p className="mt-1 text-xs text-gray-500">{help}</p>}
      {value.length > 0 && (
        <p className="mt-1 text-xs text-gray-400">{value.length} slug{value.length === 1 ? '' : 's'} configurado{value.length === 1 ? '' : 's'}</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-editors
// ---------------------------------------------------------------------------

function HeroSlidesEditor({ slides, onChange }: { slides: HeroSlide[]; onChange: (s: HeroSlide[]) => void }) {
  const update = (idx: number, field: keyof HeroSlide, value: string) => {
    const next = [...slides];
    next[idx] = { ...next[idx], [field]: value };
    onChange(next);
  };
  const remove = (idx: number) => onChange(slides.filter((_, i) => i !== idx));
  const add = () => onChange([...slides, { id: uid(), subtitle: '', title: '', description: '', image: '', slug: '' }]);
  const move = (idx: number, dir: -1 | 1) => {
    const next = [...slides];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange(next);
  };

  return (
    <div className="space-y-4">
      {slides.map((slide, idx) => (
        <div key={slide.id} className="border border-gray-200 rounded-lg p-4 space-y-3 bg-gray-50/50">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Slide {idx + 1}</span>
            <div className="flex items-center gap-1">
              <button onClick={() => move(idx, -1)} disabled={idx === 0} className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30">
                <ArrowsUpDownIcon className="w-4 h-4 rotate-180" />
              </button>
              <button onClick={() => move(idx, 1)} disabled={idx === slides.length - 1} className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30">
                <ArrowsUpDownIcon className="w-4 h-4" />
              </button>
              <button onClick={() => remove(idx)} className="p-1 text-red-400 hover:text-red-600">
                <TrashIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Subtitulo</label>
              <input type="text" value={slide.subtitle} onChange={(e) => update(idx, 'subtitle', e.target.value)} className={inputClass} placeholder="Ej: Granada Paisajes" />
            </div>
            <div>
              <label className={labelClass}>Slug (enlace)</label>
              <input type="text" value={slide.slug} onChange={(e) => update(idx, 'slug', e.target.value)} className={inputClass} placeholder="geoparque-de-granada" />
            </div>
          </div>
          <div>
            <label className={labelClass}>Titulo</label>
            <input type="text" value={slide.title} onChange={(e) => update(idx, 'title', e.target.value)} className={inputClass} placeholder="Titulo principal del slide" />
          </div>
          <div>
            <label className={labelClass}>Descripcion</label>
            <textarea value={slide.description} onChange={(e) => update(idx, 'description', e.target.value)} rows={2} className={inputClass} placeholder="Texto descriptivo del slide..." />
          </div>
          <div>
            <label className={labelClass}>Imagen</label>
            <ImageUploadField value={slide.image} onChange={(url) => update(idx, 'image', url)} />
          </div>
        </div>
      ))}
      <button onClick={add} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-vida-600 border border-vida-300 rounded-lg hover:bg-vida-50 transition-colors">
        <PlusIcon className="w-4 h-4" /> Anadir slide
      </button>
    </div>
  );
}

function BentoCardsEditor({ cards, onChange }: { cards: BentoCard[]; onChange: (c: BentoCard[]) => void }) {
  const update = (idx: number, field: string, value: string) => {
    const next = [...cards];
    next[idx] = { ...next[idx], [field]: value };
    onChange(next);
  };
  const remove = (idx: number) => onChange(cards.filter((_, i) => i !== idx));
  const add = () => onChange([...cards, { id: uid(), icon: 'Star', heading: '', body: '' }]);
  // El enlace se guarda como LinkTarget de tipo URL (o se borra si queda vacío),
  // que es lo que el escaparate sabe resolver.
  const updateLink = (idx: number, href: string) => {
    const next = [...cards];
    const h = href.trim();
    next[idx] = { ...next[idx], link: h ? { type: 'url', href: h } : undefined };
    onChange(next);
  };

  return (
    <div className="space-y-4">
      {cards.map((card, idx) => (
        <div key={card.id} className="border border-gray-200 rounded-lg p-4 space-y-3 bg-gray-50/50">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Tarjeta {idx + 1} {card.image ? '(con imagen)' : '(con icono)'}</span>
            <button onClick={() => remove(idx)} className="p-1 text-red-400 hover:text-red-600">
              <TrashIcon className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Icono (Lucide)</label>
              <input type="text" value={card.icon} onChange={(e) => update(idx, 'icon', e.target.value)} className={inputClass} placeholder="Mountain, Heart, Utensils..." />
            </div>
            <div>
              <label className={labelClass}>Titulo</label>
              <input type="text" value={card.heading} onChange={(e) => update(idx, 'heading', e.target.value)} className={inputClass} />
            </div>
          </div>
          <div>
            <label className={labelClass}>Texto</label>
            <textarea value={card.body} onChange={(e) => update(idx, 'body', e.target.value)} rows={2} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Imagen (opcional, convierte en tarjeta visual)</label>
            <ImageUploadField value={card.image || ''} onChange={(url) => update(idx, 'image', url)} />
          </div>
          <div>
            <label className={labelClass}>Enlace (URL) — al pulsar la tarjeta se abre en una pestaña nueva</label>
            <input
              type="url"
              value={card.link?.href || ''}
              onChange={(e) => updateLink(idx, e.target.value)}
              className={inputClass}
              placeholder="https://..."
            />
          </div>
          {card.image && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Overlay titulo</label>
                <input type="text" value={card.overlay_heading || ''} onChange={(e) => update(idx, 'overlay_heading', e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Overlay texto</label>
                <input type="text" value={card.overlay_body || ''} onChange={(e) => update(idx, 'overlay_body', e.target.value)} className={inputClass} />
              </div>
            </div>
          )}
        </div>
      ))}
      <button onClick={add} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-vida-600 border border-vida-300 rounded-lg hover:bg-vida-50 transition-colors">
        <PlusIcon className="w-4 h-4" /> Anadir tarjeta
      </button>
    </div>
  );
}

// Columnas preparadas del procedimiento «Catálogo de producto» (VIDA-371).
const FOOTER_PRESETS: FooterColumn[] = [
  {
    heading: 'Catálogo',
    links: [
      { label: 'Ver catálogo', href: '/catalogo' },
      { label: 'Categorías', href: '/categorias' },
      { label: 'Novedades', href: '/catalogo?orden=recientes' },
      { label: 'Destacados', href: '/#destacados' },
    ],
  },
  {
    heading: 'Información',
    links: [
      { label: 'Contacto', href: '/contacto' },
      { label: 'Sobre nosotros', href: '/sobre-nosotros' },
      { label: 'Preguntas frecuentes', href: '/faq' },
    ],
  },
  {
    heading: 'Aspectos legales',
    links: [
      { label: 'Política de privacidad', href: '/privacidad' },
      { label: 'Términos y condiciones', href: '/terminos' },
      { label: 'Aviso legal', href: '/aviso-legal' },
      { label: 'Política de cookies', href: '/cookies' },
    ],
  },
];

function FooterLinksEditor({ columns, onChange }: { columns: FooterColumn[]; onChange: (c: FooterColumn[]) => void }) {
  const updateHeading = (colIdx: number, value: string) => {
    const next = [...columns];
    next[colIdx] = { ...next[colIdx], heading: value };
    onChange(next);
  };
  const updateLink = (colIdx: number, linkIdx: number, field: 'label' | 'href', value: string) => {
    const next = [...columns];
    const links = [...next[colIdx].links];
    links[linkIdx] = { ...links[linkIdx], [field]: value };
    next[colIdx] = { ...next[colIdx], links };
    onChange(next);
  };
  const removeLink = (colIdx: number, linkIdx: number) => {
    const next = [...columns];
    next[colIdx] = { ...next[colIdx], links: next[colIdx].links.filter((_, i) => i !== linkIdx) };
    onChange(next);
  };
  const addLink = (colIdx: number) => {
    const next = [...columns];
    next[colIdx] = { ...next[colIdx], links: [...next[colIdx].links, { label: '', href: '/' }] };
    onChange(next);
  };
  const removeColumn = (colIdx: number) => onChange(columns.filter((_, i) => i !== colIdx));
  const addColumn = () => onChange([...columns, { heading: 'Nueva columna', links: [] }]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {columns.map((col, colIdx) => (
          <div key={colIdx} className="border border-gray-200 rounded-lg p-4 bg-gray-50/50 space-y-3">
            <div className="flex items-center justify-between">
              <input
                type="text"
                value={col.heading}
                onChange={(e) => updateHeading(colIdx, e.target.value)}
                className="text-sm font-semibold text-gray-900 bg-transparent border-0 border-b border-gray-300 focus:ring-0 focus:border-vida-500 px-0 py-1 w-full"
              />
              <button onClick={() => removeColumn(colIdx)} className="p-1 text-red-400 hover:text-red-600 ml-2 flex-shrink-0">
                <TrashIcon className="w-3.5 h-3.5" />
              </button>
            </div>
            {col.links.map((link, linkIdx) => (
              <div key={linkIdx} className="flex items-center gap-2">
                <input type="text" value={link.label} onChange={(e) => updateLink(colIdx, linkIdx, 'label', e.target.value)} className={`flex-1 ${inputClass}`} placeholder="Etiqueta" />
                <input type="text" value={link.href} onChange={(e) => updateLink(colIdx, linkIdx, 'href', e.target.value)} className={`flex-1 ${inputClass}`} placeholder="/ruta" />
                <button onClick={() => removeLink(colIdx, linkIdx)} className="p-1 text-red-400 hover:text-red-600 flex-shrink-0">
                  <TrashIcon className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            <button onClick={() => addLink(colIdx)} className="text-xs text-vida-600 hover:text-vida-700 font-medium">
              + Anadir enlace
            </button>
          </div>
        ))}
      </div>
      {columns.length < 4 && (
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={addColumn} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-vida-600 border border-vida-300 rounded-lg hover:bg-vida-50 transition-colors">
            <PlusIcon className="w-4 h-4" /> Anadir columna
          </button>
          <span className="text-xs text-gray-500">o una preparada:</span>
          {FOOTER_PRESETS.filter((preset) => !columns.some((c) => c.heading === preset.heading)).map((preset) => (
            <button
              key={preset.heading}
              onClick={() => onChange([...columns, { heading: preset.heading, links: preset.links.map((l) => ({ ...l })) }])}
              className="px-3 py-1.5 text-xs font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              + {preset.heading}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function PagesEditor({ pages, onChange }: { pages: PageContent[]; onChange: (p: PageContent[]) => void }) {
  const [expandedSlug, setExpandedSlug] = useState<string | null>(null);

  const update = (idx: number, field: keyof PageContent, value: string | boolean) => {
    const next = [...pages];
    next[idx] = { ...next[idx], [field]: value };
    onChange(next);
  };
  const remove = (idx: number) => onChange(pages.filter((_, i) => i !== idx));
  const add = () => onChange([...pages, { slug: '', title: 'Nueva pagina', content: '', active: true }]);

  return (
    <div className="space-y-3">
      {pages.map((page, idx) => {
        const isExpanded = expandedSlug === page.slug || expandedSlug === `new-${idx}`;
        return (
          <div key={page.slug || `new-${idx}`} className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50/50">
            {/* Header row */}
            <div className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-100 transition-colors"
              onClick={() => setExpandedSlug(isExpanded ? null : (page.slug || `new-${idx}`))}
            >
              <div className="flex items-center gap-3">
                <span className={`w-2 h-2 rounded-full ${page.active ? 'bg-green-500' : 'bg-gray-300'}`} />
                <span className="text-sm font-medium text-gray-900">{page.title || 'Sin titulo'}</span>
                <span className="text-xs text-gray-400">/{page.slug}</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => { e.stopPropagation(); update(idx, 'active', !page.active); }}
                  className={`px-2 py-0.5 text-xs rounded-full font-medium ${page.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}
                >
                  {page.active ? 'Activa' : 'Inactiva'}
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); remove(idx); }}
                  className="p-1 text-red-400 hover:text-red-600"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Expanded editor */}
            {isExpanded && (
              <div className="px-4 pb-4 border-t border-gray-200 pt-3 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelClass}>Titulo de la pagina</label>
                    <input type="text" value={page.title} onChange={(e) => update(idx, 'title', e.target.value)} className={inputClass} placeholder="Sobre Nosotros" />
                  </div>
                  <div>
                    <label className={labelClass}>Slug (URL)</label>
                    <div className="flex items-center">
                      <span className="text-sm text-gray-400 mr-1">/</span>
                      <input type="text" value={page.slug} onChange={(e) => update(idx, 'slug', e.target.value)} className={inputClass} placeholder="sobre-nosotros" />
                    </div>
                  </div>
                </div>
                <div>
                  <label className={labelClass}>Contenido (soporta Markdown basico: **negrita**, saltos de linea)</label>
                  <textarea
                    value={page.content}
                    onChange={(e) => update(idx, 'content', e.target.value)}
                    rows={12}
                    className={`${inputClass} font-mono text-xs leading-relaxed`}
                    placeholder="Escribe el contenido de la pagina aqui..."
                  />
                </div>
              </div>
            )}
          </div>
        );
      })}
      <button onClick={add} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-vida-600 border border-vida-300 rounded-lg hover:bg-vida-50 transition-colors">
        <PlusIcon className="w-4 h-4" /> Anadir pagina
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main editor component
// ---------------------------------------------------------------------------

/** `catalogId` = catálogo adicional (VIDA-370); sin él, la web del principal. */
export default function CatalogSiteEditor({ catalogId = null }: { catalogId?: string | null }) {
  const [config, setConfig] = useState<CatalogSiteConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setLoading(true);
    setDirty(false);
    loadConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogId]);

  const loadConfig = async () => {
    try {
      const res = await contentApi.getCatalogSiteConfig(catalogId);
      if (res.data?.data) setConfig(res.data.data);
    } catch (e) {
      console.error('Failed to load catalog site config', e);
      toast.error('Error al cargar la configuracion del sitio');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!config) return;
    setSaving(true);
    try {
      await contentApi.saveCatalogSiteConfig(config, catalogId);
      setDirty(false);
      toast.success('Configuracion guardada correctamente');
    } catch (e) {
      console.error('Failed to save catalog site config', e);
      const reason = isAxiosError(e) ? e.response?.data?.error?.message : undefined;
      toast.error(reason ? `No se ha guardado: ${reason}` : 'Error al guardar la configuracion');
    } finally {
      setSaving(false);
    }
  };

  const patch = <K extends keyof CatalogSiteConfig>(key: K, value: CatalogSiteConfig[K]) => {
    setConfig((prev) => prev ? { ...prev, [key]: value } : prev);
    setDirty(true);
  };

  const patchNested = <K extends keyof CatalogSiteConfig>(
    key: K,
    field: string,
    value: string
  ) => {
    setConfig((prev) => {
      if (!prev) return prev;
      return { ...prev, [key]: { ...(prev[key] as Record<string, unknown>), [field]: value } };
    });
    setDirty(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-400">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!config) {
    return (
      <div className="p-8 text-center text-gray-500">
        No se pudo cargar la configuracion del sitio.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Save bar */}
      <div className="flex items-center justify-between bg-white rounded-xl border border-gray-200 px-6 py-3">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Catalogo Web</h2>
          <p className="text-sm text-gray-500">Edita los textos, imagenes, paginas y secciones del catalogo online</p>
        </div>
        <div className="flex items-center gap-3">
          {dirty && <span className="text-xs text-amber-600 font-medium">Cambios sin guardar</span>}
          <button
            onClick={handleSave}
            disabled={saving || !dirty}
            className="inline-flex items-center gap-2 px-4 py-2 bg-vida-600 text-white text-sm font-medium rounded-lg hover:bg-vida-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {saving && <LoadingSpinner size="sm" />}
            Guardar
          </button>
        </div>
      </div>

      {/* Hero Slider */}
      <Section title="Slider Inicial" subtitle="Slides del carrusel principal de la pagina de inicio" defaultOpen>
        <HeroSlidesEditor slides={config.hero_slides} onChange={(s) => patch('hero_slides', s)} />
      </Section>

      {/* Categories Grid */}
      <Section title="Grid de Categorias" subtitle="Categorias con icono y color mostradas en la home">
        <CatalogCategoriesEditor categories={config.categories_grid} onChange={(c) => patch('categories_grid', c)} ImageField={ImageUploadField} newId={uid} />
      </Section>

      {/* Bento Cards */}
      <Section title="Espacios adicionales" subtitle="Activa o desactiva espacios de la portada: categorías, llamadas a la acción, subvenciones y otros que te pueden interesar">
        <SpacesEditor value={config.spaces} onChange={(v) => patch('spaces', v)} ImageField={ImageUploadField} />
      </Section>

      <Section title="Estructura de la ficha" subtitle="Qué datos y enlaces enseña la ficha de cada enlace del catálogo">
        <ProductDetailStructureEditor value={config.product_detail} onChange={(v) => patch('product_detail', v)} />
      </Section>

      <Section title="Tarjetas Bento" subtitle="Tarjetas de contenido en la seccion 'Tierra de Contrastes'">
        <BentoCardsEditor cards={config.bento_cards} onChange={(c) => patch('bento_cards', c)} />
      </Section>

      {/* Section Headings */}
      <Section title="Titulos de Secciones" subtitle="Encabezados y textos descriptivos de cada seccion de la home">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Titulo categorias</label>
              <input type="text" value={config.home_sections.categories_heading} onChange={(e) => patchNested('home_sections', 'categories_heading', e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Titulo destacados</label>
              <input type="text" value={config.home_sections.featured_heading} onChange={(e) => patchNested('home_sections', 'featured_heading', e.target.value)} className={inputClass} />
            </div>
          </div>
          <div>
            <label className={labelClass}>Descripcion categorias</label>
            <textarea value={config.home_sections.categories_description} onChange={(e) => patchNested('home_sections', 'categories_description', e.target.value)} rows={2} className={inputClass} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Titulo bento</label>
              <input type="text" value={config.home_sections.bento_heading} onChange={(e) => patchNested('home_sections', 'bento_heading', e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Titulo "mas por descubrir"</label>
              <input type="text" value={config.home_sections.more_heading} onChange={(e) => patchNested('home_sections', 'more_heading', e.target.value)} className={inputClass} />
            </div>
          </div>
          <div>
            <label className={labelClass}>Titulo CTA final</label>
            <input type="text" value={config.home_sections.cta_heading} onChange={(e) => patchNested('home_sections', 'cta_heading', e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Descripcion CTA final</label>
            <textarea value={config.home_sections.cta_description} onChange={(e) => patchNested('home_sections', 'cta_description', e.target.value)} rows={2} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Texto boton CTA</label>
            <input type="text" value={config.home_sections.cta_button} onChange={(e) => patchNested('home_sections', 'cta_button', e.target.value)} className={inputClass} />
          </div>
        </div>
      </Section>

      {/* CTA Image */}
      <Section title="Imagen CTA" subtitle="Imagen decorativa de la seccion de llamada a la accion">
        <div>
          <label className={labelClass}>Imagen</label>
          <ImageUploadField value={config.cta_image} onChange={(url) => patch('cta_image', url)} previewSize="md" />
        </div>
      </Section>

      {/* Ideas que Transforma */}
      <Section title='Ideas que Transforma' subtitle='Lista curada de productos del catalogo que aparecen en la home. Se identifican por su "slug".'>
        <div className="space-y-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={config.show_ideas !== false}
              onChange={(e) => patch('show_ideas', e.target.checked)}
              className="rounded border-gray-300 text-vida-600 h-4 w-4"
            />
            <span className="text-sm text-gray-700">Mostrar esta sección en la web</span>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Titulo de la seccion</label>
              <input type="text" value={config.home_sections.ideas_heading || ''}
                onChange={(e) => patchNested('home_sections', 'ideas_heading', e.target.value)} className={inputClass}
                placeholder="Ideas que Transforma" />
            </div>
            <div>
              <label className={labelClass}>Subtitulo / descripcion</label>
              <input type="text" value={config.home_sections.ideas_description || ''}
                onChange={(e) => patchNested('home_sections', 'ideas_description', e.target.value)} className={inputClass}
                placeholder="Propuestas seleccionadas para inspirarte." />
            </div>
          </div>
          <SlugListEditor
            label="Slugs de productos (uno por linea)"
            help="Pega los slugs de los productos del catalogo que quieres que aparezcan aqui. El orden importa: el primero sale arriba a la izquierda. Si dejas la lista vacia, la seccion no se mostrara en la web."
            value={config.ideas_products || []}
            onChange={(arr) => patch('ideas_products', arr)}
          />
        </div>
      </Section>

      {/* Mas Soluciones */}
      <Section title='Mas Soluciones' subtitle='Segunda lista curada de productos del catalogo en la home.'>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Titulo de la seccion</label>
              <input type="text" value={config.home_sections.solutions_heading || ''}
                onChange={(e) => patchNested('home_sections', 'solutions_heading', e.target.value)} className={inputClass}
                placeholder="Mas Soluciones" />
            </div>
            <div>
              <label className={labelClass}>Subtitulo / descripcion</label>
              <input type="text" value={config.home_sections.solutions_description || ''}
                onChange={(e) => patchNested('home_sections', 'solutions_description', e.target.value)} className={inputClass}
                placeholder="Descubre mas opciones pensadas para ti." />
            </div>
          </div>
          <SlugListEditor
            label="Slugs de productos (uno por linea)"
            help="Pega los slugs de los productos del catalogo que quieres que aparezcan aqui. Lista vacia = seccion oculta."
            value={config.solutions_products || []}
            onChange={(arr) => patch('solutions_products', arr)}
          />
        </div>
      </Section>

      {/* Footer Links */}
      <Section title="Enlaces del Footer" subtitle="Columnas de enlaces en el pie de pagina del catalogo">
        <FooterLinksEditor columns={config.footer_links.columns} onChange={(cols) => patch('footer_links', { columns: cols })} />
      </Section>

      {/* Pages */}
      <Section title="Paginas del Sitio" subtitle="Contenido de las paginas estaticas (Sobre nosotros, Contacto, FAQ, Legal, etc.)">
        <PagesEditor pages={config.pages || []} onChange={(p) => patch('pages', p)} />
      </Section>

      {/* Page Metadata */}
      <Section title="Metadatos de Pagina" subtitle="Titulo y descripcion para SEO del sitio">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Titulo por defecto</label>
              <input type="text" value={config.page_meta.title} onChange={(e) => patchNested('page_meta', 'title', e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Plantilla titulo</label>
              <input type="text" value={config.page_meta.title_template} onChange={(e) => patchNested('page_meta', 'title_template', e.target.value)} className={inputClass} placeholder="%s | Catalogo" />
            </div>
          </div>
          <div>
            <label className={labelClass}>Meta descripcion</label>
            <textarea value={config.page_meta.description} onChange={(e) => patchNested('page_meta', 'description', e.target.value)} rows={2} className={inputClass} />
          </div>
        </div>
      </Section>

      {/* UI Strings */}
      <Section title="Textos de Interfaz" subtitle="Labels, placeholders y textos fijos del catalogo y detalle de producto">
        <div className="space-y-4">
          <p className="text-xs text-gray-500">Estos textos aparecen en la interfaz del catalogo (pagina de listado, detalle de producto, navegacion y footer).</p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Subtitulo pagina catalogo</label>
              <input type="text" value={config.ui_strings.catalog_subtitle} onChange={(e) => patchNested('ui_strings', 'catalog_subtitle', e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Placeholder busqueda</label>
              <input type="text" value={config.ui_strings.search_placeholder} onChange={(e) => patchNested('ui_strings', 'search_placeholder', e.target.value)} className={inputClass} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Sufijo resultados</label>
              <input type="text" value={config.ui_strings.results_suffix} onChange={(e) => patchNested('ui_strings', 'results_suffix', e.target.value)} className={inputClass} placeholder="paisajes encontrados" />
            </div>
            <div>
              <label className={labelClass}>Descripcion footer</label>
              <input type="text" value={config.ui_strings.footer_description} onChange={(e) => patchNested('ui_strings', 'footer_description', e.target.value)} className={inputClass} />
            </div>
          </div>
          <div className="border-t border-gray-200 pt-4 mt-4">
            <p className="text-xs font-semibold text-gray-600 mb-3 uppercase tracking-wider">Detalle de producto</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Titulo "Sobre este..."</label>
                <input type="text" value={config.ui_strings.detail_about_heading} onChange={(e) => patchNested('ui_strings', 'detail_about_heading', e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Titulo ficha tecnica</label>
                <input type="text" value={config.ui_strings.detail_specs_heading} onChange={(e) => patchNested('ui_strings', 'detail_specs_heading', e.target.value)} className={inputClass} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-3">
              <div>
                <label className={labelClass}>Titulo CTA lateral</label>
                <input type="text" value={config.ui_strings.detail_cta_heading} onChange={(e) => patchNested('ui_strings', 'detail_cta_heading', e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Titulo relacionados</label>
                <input type="text" value={config.ui_strings.detail_related_heading} onChange={(e) => patchNested('ui_strings', 'detail_related_heading', e.target.value)} className={inputClass} />
              </div>
            </div>
            <div className="mt-3">
              <label className={labelClass}>Texto CTA lateral</label>
              <textarea value={config.ui_strings.detail_cta_body} onChange={(e) => patchNested('ui_strings', 'detail_cta_body', e.target.value)} rows={2} className={inputClass} />
            </div>
          </div>
          <div className="border-t border-gray-200 pt-4 mt-4">
            <p className="text-xs font-semibold text-gray-600 mb-3 uppercase tracking-wider">Navegacion</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Label "Inicio"</label>
                <input type="text" value={config.ui_strings.nav_home} onChange={(e) => patchNested('ui_strings', 'nav_home', e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Label "Catalogo"</label>
                <input type="text" value={config.ui_strings.nav_catalog} onChange={(e) => patchNested('ui_strings', 'nav_catalog', e.target.value)} className={inputClass} />
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Bottom save button */}
      {dirty && (
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-vida-600 text-white text-sm font-medium rounded-lg hover:bg-vida-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {saving && <LoadingSpinner size="sm" />}
            Guardar cambios
          </button>
        </div>
      )}
    </div>
  );
}
